/**
 * Robust CV upload helper — battle-tested 4-fallback chain extracted from
 * CVManager so both `/cv` (returning users) and `/onboarding/cv` (new users)
 * share the same upload behavior. Returns the uploaded CV object on success
 * or throws an Error with a human-readable message.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAME_ORIGIN_API = `${typeof window !== 'undefined' ? window.location.origin : ''}/api`;

/**
 * @param {File} file - the chosen file (PDF/DOCX/TXT, ≤50MB)
 * @param {string} authToken - JWT
 * @param {(pct: number) => void} [onProgress]
 * @returns {Promise<{ id: string, filename: string, [k: string]: any }>}
 */
export async function uploadCv(file, authToken, onProgress) {
  if (!file) throw new Error('No file selected');

  const validTypes = ['.pdf', '.docx', '.txt'];
  const fileExt = '.' + file.name.split('.').pop().toLowerCase();
  if (!validTypes.includes(fileExt)) {
    throw new Error('Unsupported file type. Please upload PDF, DOCX, or TXT.');
  }
  if (file.size > 50 * 1024 * 1024) {
    throw new Error('File too large. Maximum size is 50MB.');
  }

  const authHeader = `Bearer ${authToken}`;
  const setPct = (n) => onProgress?.(Math.min(Math.max(Math.round(n), 0), 100));

  // METHOD 1: Same-origin multipart (bypasses CORS + Cloudflare)
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${SAME_ORIGIN_API}/cv/upload`, fd, {
      headers: { Authorization: authHeader },
      timeout: 60000,
      onUploadProgress: (e) => setPct(((e.loaded || 0) * 95) / (e.total || file.size)),
    });
    setPct(100);
    return res.data;
  } catch (_) { /* fall through */ }

  // METHOD 2: Cross-origin multipart
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/cv/upload`, fd, {
      headers: { Authorization: authHeader },
      timeout: 60000,
      onUploadProgress: (e) => setPct(((e.loaded || 0) * 95) / (e.total || file.size)),
    });
    setPct(100);
    return res.data;
  } catch (_) { /* fall through */ }

  // METHODS 3–4: Read to base64 (arrayBuffer first, FileReader fallback) then JSON POST
  let base64;
  try {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 8192) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
    }
    base64 = btoa(binary);
  } catch (_) {
    base64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || '').split(',')[1] || '');
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
  }

  const lastResort = await axios.post(
    `${SAME_ORIGIN_API}/cv/upload-base64`,
    { filename: file.name, content_base64: base64, mime_type: file.type || 'application/pdf' },
    { headers: { Authorization: authHeader }, timeout: 120000 },
  );
  setPct(100);
  return lastResort.data;
}
